import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Stack, Files, ChartBar, Sparkle } from "@phosphor-icons/react";
import ProgramListItem from "./ProgramListItem";
import WorkoutDialog from "./WorkoutDialog";
import ProgramDialog from "./ProgramDialog";
import CopyWorkoutDialog from "./CopyWorkoutDialog";
import FromTemplateDialog from "./FromTemplateDialog";
import UnassignedWorkoutsList from "./workouts/UnassignedWorkoutsList";
import ProgramComparisonDialog from "./workouts/ProgramComparisonDialog";
import AIWorkoutGenDialog from "./AIWorkoutGenDialog";
import useWorkoutsTab from "./workouts/useWorkoutsTab";

export default function WorkoutsTab({ clientId }) {
  const t = useWorkoutsTab(clientId);
  const [showCompare, setShowCompare] = useState(false);
  const [showAIGen, setShowAIGen] = useState(false);

  const unassignedWorkouts = t.workouts
    .filter((w) => !w.program_id)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="workouts-tab">
      <div className="flex items-center justify-between mb-6">
        <h3
          className="text-xl font-bold tracking-tight uppercase text-white"
          style={{ fontFamily: "Barlow Condensed, sans-serif" }}
        >
          PROGRAMMI E SCHEDE
        </h3>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setShowAIGen(true)}
            variant="outline"
            className="border-[#AF52DE]/40 text-[#AF52DE] hover:bg-[#AF52DE]/10 hover:text-[#AF52DE]"
            data-testid="ai-generate-workout-button"
          >
            <Sparkle size={18} weight="fill" className="mr-2" /> Genera con AI
          </Button>
          <Button
            onClick={t.openNewProgram}
            variant="outline"
            className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A] hover:text-white"
            data-testid="add-program-button"
          >
            <Stack size={18} className="mr-2" /> Nuovo Programma
          </Button>
          {t.programs.length >= 2 && (
            <Button
              onClick={() => setShowCompare(true)}
              variant="outline"
              className="border-[#AF52DE]/40 text-[#AF52DE] hover:bg-[#AF52DE]/10 hover:text-[#AF52DE]"
              data-testid="compare-programs-button"
            >
              <ChartBar size={18} className="mr-2" /> Confronta
            </Button>
          )}
          <Button
            onClick={() => t.openFromTemplate("")}
            variant="outline"
            className="border-[#D4FF00]/40 text-[#D4FF00] hover:bg-[#D4FF00]/10 hover:text-[#D4FF00]"
            data-testid="from-template-button"
          >
            <Files size={18} className="mr-2" /> Da Template
          </Button>
          <Button
            onClick={() => t.openNewWorkout("")}
            className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
            data-testid="add-workout-button"
          >
            <Plus size={18} className="mr-2" /> Nuova Scheda
          </Button>
        </div>
      </div>

      {/* Programs */}
      {t.programs.length > 0 && (
        <div className="space-y-4 mb-6">
          {t.programs.map((program, i) => {
            const programWorkouts = t.workouts
              .filter((w) => w.program_id === program.id)
              .sort((a, b) => (a.order || 0) - (b.order || 0));
            return (
              <ProgramListItem
                key={program.id}
                program={program}
                index={i}
                workouts={programWorkouts}
                isExpanded={!!t.expandedPrograms[program.id]}
                completed={t.isProgramCompleted(program)}
                getPhaseTitle={t.getPhaseTitle}
                exerciseLibrary={t.exerciseLibrary}
                userMappings={t.userMappings}
                onToggle={t.toggleProgram}
                onAddWorkout={t.openNewWorkout}
                onEditProgram={t.openEditProgram}
                onDeleteProgram={t.handleDeleteProgram}
                onToggleVisibility={t.handleToggleVisibility}
                onEditWorkout={t.openEditWorkout}
                onDeleteWorkout={t.handleDeleteWorkout}
                onCopyWorkout={t.openCopyWorkout}
                onSaveWorkoutAsTemplate={t.handleSaveAsTemplate}
                onWorkoutDragEnd={t.handleWorkoutDragEnd}
                onLibraryUpdated={t.fetchData}
              />
            );
          })}
        </div>
      )}

      <UnassignedWorkoutsList
        unassignedWorkouts={unassignedWorkouts}
        hasPrograms={t.programs.length > 0}
        getPhaseTitle={t.getPhaseTitle}
        exerciseLibrary={t.exerciseLibrary}
        userMappings={t.userMappings}
        onEdit={t.openEditWorkout}
        onDelete={t.handleDeleteWorkout}
        onCopy={t.openCopyWorkout}
        onSaveAsTemplate={t.handleSaveAsTemplate}
        onDragEnd={t.handleWorkoutDragEnd}
      />

      {t.programs.length === 0 && t.workouts.length === 0 && (
        <div className="text-center py-16 bg-[#171717] border border-[#262626] rounded-lg">
          <Stack size={40} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">Nessun programma o scheda</p>
          <p className="text-zinc-600 text-sm mt-1">
            Crea un programma per organizzare le schede di allenamento
          </p>
        </div>
      )}

      <ProgramDialog
        open={t.showProgramDialog}
        onOpenChange={t.setShowProgramDialog}
        editing={t.editingProgram}
        form={t.programForm}
        setForm={t.setProgramForm}
        phases={t.phases}
        onSave={t.handleSaveProgram}
      />

      <WorkoutDialog
        open={t.showWorkoutDialog}
        onOpenChange={t.setShowWorkoutDialog}
        editing={t.editingWorkout}
        form={t.workoutForm}
        setForm={t.setWorkoutForm}
        programs={t.programs}
        phases={t.phases}
        onSave={t.handleSaveWorkout}
      />

      <CopyWorkoutDialog
        workout={t.copyingWorkout}
        programs={t.programs}
        targetProgramId={t.copyTargetProgram}
        setTargetProgramId={t.setCopyTargetProgram}
        onCancel={() => t.setCopyingWorkout(null)}
        onConfirm={t.handleCopyWorkout}
      />

      <FromTemplateDialog
        open={t.showFromTemplate}
        onOpenChange={t.setShowFromTemplate}
        programs={t.programs}
        targetProgramId={t.fromTemplateProgramId}
        setTargetProgramId={t.setFromTemplateProgramId}
        onConfirm={t.handleInstantiateTemplate}
      />

      <ProgramComparisonDialog
        open={showCompare}
        onOpenChange={setShowCompare}
        programs={t.programs}
        workouts={t.workouts}
        exerciseLibrary={t.exerciseLibrary}
        userMappings={t.userMappings}
      />

      {showAIGen && <AIWorkoutGenDialog onClose={() => setShowAIGen(false)} clientId={clientId} />}
    </div>
  );
}
